/**
 * FeatureErrorFallback — fallback affiché par React Router quand une feature
 * crashe (erreur de loader OU erreur de rendering dans ses composants).
 *
 * @remarks
 * Filet intermédiaire entre `PanelErrorBoundary` (panneau) et
 * `RootErrorBoundary` (app entière) :
 *
 *  RootErrorBoundary   → catastrophe app-level (provider crashé)
 *    ↓
 *  FeatureErrorFallback → une feature crashe (loader 500, render error)
 *    ↓                   → seule cette feature affiche un fallback,
 *                          la sidebar + autres features restent navigables
 *    ↓
 *  PanelErrorBoundary  → un panel d'une feature crashe
 *
 * Branchement : chaque feature wrap ses routes dans
 * `{ element: <Outlet />, errorElement: <FeatureErrorFallback featureId="X" />, children: [...] }`.
 */

import { useRouteError, isRouteErrorResponse, useNavigate } from 'react-router-dom';
import { AlertOctagon, RotateCcw, ArrowLeft } from 'lucide-react';
import { Heading, Button } from '@/shared/ui';

interface Props {
  /** Identifiant de la feature pour le log + UI ("tech-tests", "admin-evaluations"...). */
  featureId: string;
  /** Label utilisateur affiché ("Évaluations", "Tests"...). */
  featureLabel?: string;
}

export function FeatureErrorFallback({ featureId, featureLabel }: Props) {
  const error = useRouteError();
  const navigate = useNavigate();

  // Log pour télémétrie / debug (Sentry, etc. en prod).
  console.error(`[FeatureErrorFallback ${featureId}]`, error);

  const message = formatErrorMessage(error);
  const label = featureLabel ?? featureId;

  return (
    <div className="text-fg-secondary flex h-full flex-col items-center justify-center gap-4 p-8">
      <AlertOctagon className="text-warning size-10" aria-hidden />

      <div className="max-w-md text-center">
        <Heading level="section">La feature « {label} » a planté</Heading>
        <p
          className="text-fg-muted mt-2 text-sm"
          style={{ lineHeight: 'var(--line-height-normal)' }}
        >
          Le reste de l'application reste utilisable. Tu peux essayer de recharger cette feature ou
          revenir au tableau de bord.
        </p>
      </div>

      {message && (
        <pre className="bg-bg-subtle text-fg-muted border-border max-w-xl overflow-auto rounded-md border p-3 text-xs">
          {message}
        </pre>
      )}

      <div className="mt-2 flex gap-2">
        <button
          type="button"
          onClick={() => {
            window.location.reload();
          }}
          className="border-border-strong hover:bg-bg-hover inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm"
        >
          <RotateCcw className="size-3.5" aria-hidden />
          Recharger
        </button>
        <Button
          variant="primary"
          size="md"
          onClick={() => {
            void navigate('/');
          }}
        >
          <ArrowLeft className="size-3.5" aria-hidden />
          Retour à l'accueil
        </Button>
      </div>
    </div>
  );
}

function formatErrorMessage(error: unknown): string | null {
  if (isRouteErrorResponse(error)) {
    return `${String(error.status)} ${error.statusText}${error.data ? ` — ${String(error.data)}` : ''}`;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return null;
}
